'use client';
import { useState } from 'react';
import { stories } from '@/lib/data';
import TabSwitcher from '@/components/ui/TabSwitcher';
import FilterBar from '@/components/ui/FilterBar';
import LoadMoreButton from '@/components/ui/LoadMoreButton';
import Link from 'next/link';
import Label from '@/components/ui/Label';

const latestTabs = [
  { id: 'mostread', label: 'Most Read' },
  { id: 'latest', label: 'Latest' },
  { id: 'trending', label: 'Trending' },
  { id: 'saved', label: 'Saved' },
];

const sections = ['All', 'World', 'Politics', 'Business', 'Technology', 'Climate', 'Sport', 'Culture'];

export default function LatestPage() {
  const [activeTab, setActiveTab] = useState('mostread');
  const [activeSection, setActiveSection] = useState('All');

  const filtered = stories.filter(s => activeSection === 'All' || s.section === activeSection);

  const displayedStories = [...filtered].sort((a, b) => {
    if (activeTab === 'mostread' || activeTab === 'trending') {
      return (b.readCount || 0) - (a.readCount || 0);
    }
    return 0; // latest — keep order for mock data
  });

  return (
    <div className="max-w-[1400px] mx-auto px-5 py-8">
      <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[36px] mb-6 text-[#0D0D0D]">
        Latest & Most Read
      </h1>

      <TabSwitcher tabs={latestTabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <FilterBar filters={sections} activeFilter={activeSection} onFilterChange={setActiveSection} />

      {activeTab === 'saved' ? (
        <div className="py-16 text-center border border-[#D4D4D4] max-w-[600px] mx-auto">
          <p className="font-['Playfair_Display',Georgia,serif] text-[22px] mb-3 text-[#0D0D0D]">Your saved stories appear here</p>
          <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] text-[#6B6B6B]">Save stories by clicking the bookmark icon on any article.</p>
        </div>
      ) : (
        <div className="max-w-[900px]">
          {activeTab === 'latest' && (
            <div className="sticky top-[100px] bg-[#F5F4F0] py-2 px-4 z-10 font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.06em] text-[#2D2D2D] mb-0">
              Today — {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          )}

          {displayedStories.slice(0, 15).map((story, index) => (
            <Link key={story.id} href={`/article/${story.slug}`} className="block">
              <div className="grid grid-cols-[70px_1fr_80px] border-b border-[#D4D4D4] py-4 hover:opacity-75 transition-opacity cursor-pointer group">
                {/* Left col */}
                <div className="border-r border-[#D4D4D4] pr-4 flex items-start pt-1">
                  {activeTab === 'mostread' || activeTab === 'trending' ? (
                    <span className="font-['Playfair_Display',Georgia,serif] font-bold text-[24px] text-[#D4D4D4] leading-none w-full text-right">
                      {index + 1}
                    </span>
                  ) : (
                    <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] text-[#0D0D0D] text-right w-full leading-tight">
                      {story.publishedAt.includes('hour') ? story.publishedAt.replace(' hours ago', 'h').replace(' hour ago', 'h') : story.publishedAt}
                    </span>
                  )}
                </div>
                {/* Middle col */}
                <div className="px-5">
                  <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.1em] uppercase text-[#C8102E]">
                    {story.section}
                  </span>
                  {story.label && ['analysis', 'opinion', 'explainer', 'feature'].includes(story.label) && (
                    <span className="ml-2"><Label type={story.label} /></span>
                  )}
                  <h3 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[17px] leading-[1.35] mt-1 group-hover:text-[#C8102E] transition-colors">
                    {story.title}
                  </h3>
                  <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] mt-1.5">
                    {story.author} · {story.readingTime} min read
                    {activeTab === 'mostread' && story.readCount && (
                      <span className="ml-2">· {(story.readCount / 1000).toFixed(0)}k reads</span>
                    )}
                  </div>
                </div>
                {/* Right col (thumbnail) */}
                <div className={`${story.imageBg} w-20 h-14 shrink-0`} />
              </div>
            </Link>
          ))}
          <LoadMoreButton />
        </div>
      )}
    </div>
  );
}
